import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Order, Asset, AssetRepositoryService, SelectOption, AssetListComponent, OrderManagerService, OrderLogForCreation, OrderLogsRepositoryService, ValidationProblem, UIConfigurationService, AssetUIConfiguration, eOrderType, OrderRepositoryService, OrderStateRepositoryService, eOrderStatusType } from 'reg-hub-common';
import { BehaviorSubject, filter, forkJoin, map, mapTo, Observable, Subject, takeUntil } from 'rxjs';
import { ClientAuthService } from '../../services/auth/client-auth.service';
import { OrdersHubService } from '../../services/hubs/orders-hub/orders-hub.service';
import { AssetSearchNotificationComponent } from '../../navigation/asset-search-notification/asset-search-notification.component';

@Component({
    selector: 'app-assets',
    templateUrl: './assets.component.html',
    styleUrls: ['./assets.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssetsComponent implements OnInit, OnDestroy {
  @ViewChild(AssetListComponent) assetListComponent!: AssetListComponent;

  @Input() errors$!: Observable<ValidationProblem[] | undefined>;
  @Input() uiConfiguration!: AssetUIConfiguration;
  order!: Order;

  @Output() assetFormValueChangedEvent = new EventEmitter<Asset>();

  assets$ = new BehaviorSubject<Asset[]>([]);

  private onDestroy$ = new Subject<void>();

  protected isEditable: boolean = true;
  protected assetTypes: SelectOption[] = [];

  protected expandedAsset: Asset | null = null;

  constructor(private assetRepo: AssetRepositoryService,
    private orderManager: OrderManagerService,
    private orderLogService: OrderLogsRepositoryService,
    private authService: ClientAuthService,
    private orderRepositoryService: OrderRepositoryService,
    private orderStateService: OrderStateRepositoryService,
    private orderHub: OrdersHubService) { }

  ngOnInit(): void {
    this.order = this.orderManager.currentOrder.value;
    this.assets$.next(this.order!.assets ?? []);
    this.orderManager.currentOrder
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(order => this.order = order);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  handleAssetSaveEvent(savedAsset: Asset) {
    // if we don't have an ID yet, create the Asset
    if (!savedAsset.id) {
      this.createAsset(savedAsset);
    }
    // otherwise, the Asset already exists, so we should update it.
    else {
      this.updateAsset(savedAsset);
    }
  }

  handleAssetRemovedEvent(removedAsset: Asset) {
    this.assetRepo.deleteAsset(`orders/${this.order.id}/assets/${removedAsset.id}`)
      .subscribe(() => {
        const index = this.order.assets!.findIndex(obj => obj.id === removedAsset.id || !obj.id);

        // as long as our index was found 
        if (index != -1) {
          this.order.assets!.splice(index, 1);
          this.orderManager.updateOrder(this.order);
          this.assets$.next(this.order.assets ?? []);
        }
      });
  }

  handleAllAssetsRemovedEvent() {
    this.orderManager.savingAsset$.next(true);

    this.assetRepo.deleteAssets(this.order.id).subscribe(() => {
      this.order.assets = [];
      this.orderManager.updateOrder(this.order);
      this.assets$.next([]);
      this.orderManager.savingAsset$.next(false);
    });
  }

  handleAssetCopiedEvent(copiedAsset: Asset) {
    // create a full copy of the party so we don't overwrite the party from which we copied
    const copiedAssetWithoutId = { ...copiedAsset };
    copiedAssetWithoutId.id = "";
    delete (copiedAssetWithoutId.originalEntityID);
    this.handleAssetAddedEvent(copiedAssetWithoutId);
  }

  handleVinDecodedEvent(orderLog: OrderLogForCreation) {
    orderLog.username = this.authService.getUser()?.profile.name ?? "";
    this.orderLogService.createLog(this.order.id, orderLog).subscribe();
  }

  handleAssetFormValueChange(asset: Asset) {
    this.assetFormValueChangedEvent.emit(asset);
  }

  handleAssetAddedEvent(asset: Asset) {
    this.createAsset(asset);
  }
  
  handleAssetsUploadedEvent(assets: Asset[]) {
    this.orderManager.savingAsset$.next(true);

    this.assetRepo.createAssets(this.order.id, assets).subscribe((result: any) => {
      const assets = (result as any[]).map(res => res.resource as Asset);
      assets.forEach(a => this.order.assets!.push(a));
      this.orderManager.updateOrder(this.order);
      this.assets$.next(this.order.assets ?? []);
      this.orderManager.savingAsset$.next(false);
    })
  }

  private createAsset(asset: Asset) {
    this.orderManager.savingAsset$.next(true);

    this.assetRepo.createAsset(`orders/${this.order.id}/assets`, asset)
      .subscribe(result => {
        const asset = ((result as any).resource) as Asset;

        this.order.assets!.push(asset);
        this.orderManager.updateOrder(this.order);
        this.assets$.next(this.order.assets ?? []);
        this.expandedAsset = asset;
        this.orderManager.savingAsset$.next(false);
      });
  }

  private updateAsset(asset: Asset) {
    this.orderManager.savingAsset$.next(true);

    this.assetRepo.updateAsset(`orders/${this.order.id}/assets/${asset.id}`, asset)
      .subscribe(result => {
        const asset = ((result as any).resource) as Asset;

        const index = this.order.assets!.findIndex(obj => obj.id === asset.id);

        // as long as our index was found 
        if (index != -1) {
          this.order.assets![index] = asset;
          this.orderManager.updateOrder(this.order);
          this.assets$.next(this.order.assets ?? []);
        }

        this.orderManager.savingAsset$.next(false);
      });
  }

  protected replaceAsset(asset: Asset) {
    const index = this.order.assets!.findIndex(obj => obj.id === asset.id);

    // as long as our index was found 
    if (index != -1) {
      this.order.assets![index] = asset;
      this.orderManager.updateOrder(this.order);
    }
  }

  onSaving(): void {
    var assetToReplace = this.assetListComponent.onSaving();
    if (assetToReplace) {
      this.replaceAsset(assetToReplace!);
    }
  }

  handleAssetSearchRequestedEvent(serialNumber: any) {
    const newOrder = {
      referenceNumber: this.order.referenceNumber,
      jurisdiction: this.order.jurisdiction,
      country: this.order.country,
      orderTypeID: eOrderType.assetSearch,
      assets: [{ serialNumber: serialNumber }],
      searchParameters: {}
    }

    this.orderRepositoryService.createNewOrder(newOrder).subscribe(response => {
      //Submits order and connects to the orders hub at the same time
      //On completion of both operations, watches the submitted asset search order for completion
      forkJoin([
        this.orderStateService.updateOrderState(response.resource.id, { newStatus: eOrderStatusType.pending, message: 'Submitted asset search from add asset flow.' }),
        this.orderHub.connect()
      ]).subscribe({
        complete: () => this.orderHub.watchOrder(response.resource, {
          shouldViewOrderOpenNewTab: true
        })
      });
    });
  }
}
